import { PAGINATION_ITEM_TYPE } from './Pagination.constants'
import type { PaginationHelperArgs } from './Pagination.types'

export class PaginationHelper {
  boundaryCount: number
  count: number
  page: number
  siblingCount: number

  constructor({ boundaryCount, count, page, siblingCount }: PaginationHelperArgs) {
    this.boundaryCount = boundaryCount
    this.count = count
    this.page = page
    this.siblingCount = siblingCount
  }

  range(start: number, end: number): number[] {
    const length = end - start + 1
    return Array.from({ length }, (_, i) => start + i)
  }

  getStartPages(): number[] {
    return this.range(1, Math.min(this.boundaryCount, this.count))
  }

  getEndPages(): number[] {
    return this.range(
      Math.max(this.count - this.boundaryCount + 1, this.boundaryCount + 1),
      this.count
    )
  }

  getSibilingsStart(): number {
    return Math.max(
      Math.min(
        // Natural start
        this.page - this.siblingCount,
        // Lower boundary when page is high
        this.count - this.boundaryCount - this.siblingCount * 2 - 1
      ),
      // Greater than startPages
      this.boundaryCount + 2
    )
  }

  getSibilingsEnd(): number {
    const endPages = this.getEndPages()

    return Math.min(
      Math.max(
        // Natural end
        this.page + this.siblingCount,
        // Upper boundary when page is low
        this.boundaryCount + this.siblingCount * 2 + 2
      ),
      // Less than endPages
      endPages.length > 0 ? endPages[0] - 2 : this.count - 1
    )
  }

  getStartEllipsis(): number[] | string[] {
    const siblingsStart = this.getSibilingsStart()

    if (siblingsStart > this.boundaryCount + 2) {
      return [PAGINATION_ITEM_TYPE.startEllipsis]
    }

    if (this.boundaryCount + 1 < this.count - this.boundaryCount) {
      return [this.boundaryCount + 1]
    }

    return []
  }

  getEndEllipsis(): number[] | string[] {
    const siblingsEnd = this.getSibilingsEnd()

    if (siblingsEnd < this.count - this.boundaryCount - 1) {
      return [PAGINATION_ITEM_TYPE.endEllipsis]
    }

    if (this.count - this.boundaryCount > this.boundaryCount) {
      return [this.count - this.boundaryCount]
    }

    return []
  }
}
